import React, { useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { UserRole } from '../types';

interface LoginProps {
  onNavigate: (page: string) => void;
}

const Login: React.FC<LoginProps> = ({ onNavigate }) => {
  const { login } = useAuth();
  const [isRegistering, setIsRegistering] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    businessName: '',
    role: 'CLINIC' as UserRole,
    // Registration fields
    contactName: '',
    phone: '',
    address: '',
    city: '',
    state: '',
    zip: ''
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setLoading(true);

    try {
      if (formData.email && formData.password) {
        const actualRole = formData.email.includes('admin') ? 'ADMIN' : formData.role;
        
        const additionalData = isRegistering ? {
          contactName: formData.contactName,
          phone: formData.phone,
          address: formData.address,
          city: formData.city,
          state: formData.state,
          zip: formData.zip
        } : undefined;

        await login(formData.email, actualRole as UserRole, formData.businessName, {
            password: formData.password,
            ...additionalData
        });
        onNavigate('home');
      }
    } catch (err: any) {
      console.error('Login failed:', err);
      setError(err.message || 'Authentication failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-2xl w-full bg-white rounded-2xl shadow-lg p-8">
        <h2 className="text-3xl font-black text-slate-900 mb-2 text-center">
          {isRegistering ? 'Create Account' : 'Sign In'}
        </h2>
        <p className="text-slate-500 text-center mb-8">
            {isRegistering ? 'Join our dental supply network' : 'Welcome back to Piggy Dental'}
        </p>

        {error && (
            <div className="mb-6 p-4 bg-red-50 border-l-4 border-red-500 text-red-700 text-sm font-bold rounded-r-lg animate-fade-in">
                {error}
            </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="grid md:grid-cols-2 gap-6">
            <div className="md:col-span-2">
              <label className="block text-sm font-bold text-slate-700 mb-2">Email Address</label>
              <input
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({...formData, email: e.target.value})}
                className="w-full px-4 py-3 border border-slate-200 rounded-xl focus:outline-none focus:border-blue-500"
                required
              />
            </div>

            <div className="md:col-span-2">
              <div className="flex justify-between items-center mb-2">
                <label className="block text-sm font-bold text-slate-700">Password</label>
                {!isRegistering && (
                  <button 
                    type="button" 
                    onClick={() => onNavigate('forgot-password')}
                    className="text-xs font-black uppercase tracking-widest text-slate-400 hover:text-blue-600 transition-colors"
                  >
                    Forgot?
                  </button>
                )}
              </div>
              <input
                type="password"
                value={formData.password}
                onChange={(e) => setFormData({...formData, password: e.target.value})}
                className="w-full px-4 py-3 border border-slate-200 rounded-xl focus:outline-none focus:border-blue-500"
                placeholder={isRegistering ? "At least 8 characters" : "Enter your password"}
                required
                minLength={8}
              />
            </div>

            <div className={isRegistering ? "" : "hidden"}>
              <label className="block text-sm font-bold text-slate-700 mb-2">Business Name</label>
              <input
                type="text"
                value={formData.businessName}
                onChange={(e) => setFormData({...formData, businessName: e.target.value})}
                className="w-full px-4 py-3 border border-slate-200 rounded-xl focus:outline-none focus:border-blue-500"
                placeholder="Practice or lab name"
                required={isRegistering}
              />
            </div>

            <div className={isRegistering ? "" : "hidden"}>
              <label className="block text-sm font-bold text-slate-700 mb-2">Business Type</label>
              <select
                value={formData.role}
                onChange={(e) => setFormData({...formData, role: e.target.value as UserRole})}
                className="w-full px-4 py-3 border border-slate-200 rounded-xl focus:outline-none focus:border-blue-500"
              >
                <option value="CLINIC">Dental Clinic</option>
                <option value="LAB">Dental Laboratory</option>
              </select>
            </div>
            
            {isRegistering && (
              <>
                <div className="md:col-span-2 border-t border-slate-100 my-2 pt-4">
                  <h3 className="text-sm font-black uppercase text-slate-400 tracking-wider mb-4">Contact Information</h3>
                </div>

                <div>
                    <label className="block text-sm font-bold text-slate-700 mb-2">Contact Name</label>
                    <input
                    type="text"
                    value={formData.contactName}
                    onChange={(e) => setFormData({...formData, contactName: e.target.value})}
                    className="w-full px-4 py-3 border border-slate-200 rounded-xl focus:outline-none focus:border-blue-500"
                    placeholder="Full Name"
                    required
                    />
                </div>

                <div>
                    <label className="block text-sm font-bold text-slate-700 mb-2">Phone Number</label>
                    <input
                    type="tel"
                    value={formData.phone}
                    onChange={(e) => setFormData({...formData, phone: e.target.value})}
                    className="w-full px-4 py-3 border border-slate-200 rounded-xl focus:outline-none focus:border-blue-500"
                    placeholder="(555) 555-5555"
                    required
                    />
                </div>

                <div className="md:col-span-2">
                    <label className="block text-sm font-bold text-slate-700 mb-2">Address</label>
                    <input
                    type="text"
                    value={formData.address}
                    onChange={(e) => setFormData({...formData, address: e.target.value})}
                    className="w-full px-4 py-3 border border-slate-200 rounded-xl focus:outline-none focus:border-blue-500"
                    placeholder="Street Address"
                    required
                    />
                </div>

                <div>
                    <label className="block text-sm font-bold text-slate-700 mb-2">City</label>
                    <input
                    type="text"
                    value={formData.city}
                    onChange={(e) => setFormData({...formData, city: e.target.value})}
                    className="w-full px-4 py-3 border border-slate-200 rounded-xl focus:outline-none focus:border-blue-500"
                    required
                    />
                </div>

                <div className="grid grid-cols-2 gap-4">
                    <div>
                        <label className="block text-sm font-bold text-slate-700 mb-2">State</label>
                        <input
                        type="text"
                        value={formData.state}
                        onChange={(e) => setFormData({...formData, state: e.target.value})}
                        className="w-full px-4 py-3 border border-slate-200 rounded-xl focus:outline-none focus:border-blue-500"
                        required
                        />
                    </div>
                    <div>
                        <label className="block text-sm font-bold text-slate-700 mb-2">ZIP</label>
                        <input
                        type="text"
                        value={formData.zip}
                        onChange={(e) => setFormData({...formData, zip: e.target.value})}
                        className="w-full px-4 py-3 border border-slate-200 rounded-xl focus:outline-none focus:border-blue-500"
                        required
                        />
                    </div>
                </div>
              </>
            )}
          </div>

          <button
            type="submit"
            disabled={loading}
            className="w-full px-6 py-4 bg-blue-600 text-white rounded-xl hover:bg-blue-700 disabled:bg-slate-300 transition-colors font-bold text-lg shadow-lg shadow-blue-200 flex justify-center items-center"
          >
            {loading ? (
                <>
                    <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Verifying...
                </>
            ) : (
                isRegistering ? 'Create Account' : 'Sign In'
            )}
          </button>
        </form>

        <div className="mt-8 text-center border-t border-slate-50 pt-6">
          <p className="text-slate-500 mb-3">
            {isRegistering ? 'Already have an account?' : "Don't have an account?"}
          </p>
          <button
            onClick={() => {
                setIsRegistering(!isRegistering);
                setError(null);
            }}
            className="text-blue-600 hover:text-blue-800 font-bold"
          >
            {isRegistering ? 'Sign in to existing account' : 'Register new business account'}
          </button>
        </div>

        {!isRegistering && (
            <div className="mt-4 text-center text-xs text-slate-400">
             Secure authentication via AWS Cognito
            </div>
        )}
      </div>
    </div>
  );
};

export default Login;
